/**
 * 配信先 1 件手動追加ダイアログ。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10（Phase 22 拡張：手動追加）
 */
import { Plus, Trash2, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useCreateMailContact, useMailContacts } from '@/hooks/useMailContacts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CustomFieldRow {
  key: string;
  value: string;
}

export function ContactCreateDialog({ open, onOpenChange }: Props) {
  const createMutation = useCreateMailContact();
  const { data: existingContacts } = useMailContacts({ limit: 5000 });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [customFields, setCustomFields] = useState<CustomFieldRow[]>([]);
  const [promoteToCustomer, setPromoteToCustomer] = useState(false);

  // タグサジェスト用：既存の全タグ
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of existingContacts ?? []) {
      for (const t of c.tags ?? []) if (t.trim()) set.add(t);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [existingContacts]);

  const filteredTags = useMemo(() => {
    const k = tagInput.trim().toLowerCase();
    if (!k) return allTags;
    return allTags.filter((t) => t.toLowerCase().includes(k));
  }, [allTags, tagInput]);

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setTags([]);
      setTagInput('');
      setCustomFields([]);
      setPromoteToCustomer(false);
    }
  }, [open]);

  const addTag = (raw: string) => {
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setTags((prev) => {
      const next = [...prev];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      return next;
    });
    setTagInput('');
  };

  const toggleTag = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const removeTag = (t: string) => {
    setTags((prev) => prev.filter((x) => x !== t));
  };

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { key: '', value: '' }]);
  };
  const updateCustomField = (idx: number, patch: Partial<CustomFieldRow>) => {
    setCustomFields((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };
  const removeCustomField = (idx: number) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const emailNormalized = email.trim().toLowerCase();
  const emailValid = !!emailNormalized && EMAIL_RE.test(emailNormalized);
  const customFieldsObj = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of customFields) {
      const k = row.key.trim();
      const v = row.value.trim();
      if (k && v) out[k] = v;
    }
    return out;
  }, [customFields]);

  const handleSubmit = async () => {
    if (!emailValid) {
      toast.error('メアドの形式が不正です');
      return;
    }
    // 確定し忘れたタグも自動でタグ化
    const trailing = tagInput.trim();
    const finalTags =
      trailing && !tags.includes(trailing) ? [...tags, trailing] : tags;
    try {
      await createMutation.mutateAsync({
        name: name.trim() || undefined,
        email: emailNormalized,
        tags: finalTags.length > 0 ? finalTags : undefined,
        customFields:
          Object.keys(customFieldsObj).length > 0 ? customFieldsObj : undefined,
        promoteToCustomer: promoteToCustomer || undefined,
      });
      toast.success(
        `配信先「${name.trim() || emailNormalized}」を${
          promoteToCustomer ? '顧客として' : ''
        }追加しました`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>配信先を手動追加</DialogTitle>
          <DialogDescription>
            1 件ずつ手動で追加します。重複したメアドはエラーになります。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">名前（任意）</Label>
              <Input
                type="text"
                placeholder="例: 山田 太郎"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">メアド（必須）</Label>
              <Input
                type="email"
                placeholder="example@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={
                  email && !emailValid ? 'border-[var(--accent-red)]' : undefined
                }
              />
              {email && !emailValid && (
                <p className="text-[10px] text-[var(--accent-red)] mt-0.5">
                  メアドの形式が不正です
                </p>
              )}
            </div>
          </div>

          {/* タグ chip 入力 */}
          <div>
            <Label className="text-xs">タグ（任意・複数可）</Label>
            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="hover:text-[var(--accent-red)] -mr-0.5"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder={
                  tags.length === 0 ? 'タグを入力（Enter で確定）' : '追加タグ'
                }
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  const composing = e.nativeEvent.isComposing || e.keyCode === 229;
                  if (e.key === 'Enter' && !composing) {
                    e.preventDefault();
                    addTag(tagInput);
                  } else if (
                    e.key === 'Backspace' &&
                    tagInput === '' &&
                    tags.length > 0 &&
                    !composing
                  ) {
                    setTags((p) => p.slice(0, -1));
                  }
                }}
                onBlur={() => {
                  if (tagInput.trim()) addTag(tagInput);
                }}
                className="flex-1 min-w-[12ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            {allTags.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] text-[var(--text-muted)] mb-1">
                  既存タグから選択
                </div>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                  {filteredTags.map((t) => {
                    const sel = tags.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTag(t)}
                        className={
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors ' +
                          (sel
                            ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
                            : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border-transparent hover:border-[var(--accent)] hover:text-[var(--accent)]')
                        }
                      >
                        {sel && <span className="mr-1">✓</span>}
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* カスタムフィールド */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs !mb-0">カスタムフィールド（任意）</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addCustomField}>
                <Plus className="size-3 mr-1" />
                フィールド追加
              </Button>
            </div>
            {customFields.length === 0 ? (
              <p className="text-[11px] text-[var(--text-muted)] italic">
                追加するフィールドがあれば「+ フィールド追加」を押してください
              </p>
            ) : (
              <div className="space-y-1">
                {customFields.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="キー名（例: 職業）"
                      value={row.key}
                      onChange={(e) => updateCustomField(idx, { key: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="text"
                      placeholder="値"
                      value={row.value}
                      onChange={(e) => updateCustomField(idx, { value: e.target.value })}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomField(idx)}
                      title="このフィールドを削除"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
            <div className="flex-1 min-w-0">
              <Label className="text-xs font-semibold !mb-0.5">顧客として追加</Label>
              <p className="text-[10px] text-[var(--text-muted)]">
                オンにすると、追加と同時に{' '}
                <code className="text-[10px]">promotionLevel=&quot;customer&quot;</code>{' '}
                （顧客昇格状態）で登録されます。
              </p>
            </div>
            <Switch
              checked={promoteToCustomer}
              onChange={(e) => setPromoteToCustomer(e.target.checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!emailValid || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <UserPlus className="size-4 mr-1" />
            )}
            追加
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
